import React, { useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { peopleService } from "../../services/api";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const blankPerson = {
  name: "",
  relationship_label: "",
  email: "",
  phone: "",
  interests: "",
  notes: "",
  check_in_days: null,
  check_in_time: "18:00",
  enabled: true,
};
const blankOccasion = {
  title: "",
  person_id: null,
  kind: "birthday",
  month: 1,
  day: 1,
  year: null,
  annual: true,
  lead_days: 7,
  notes: "",
  enabled: true,
};
const localInput = (value) => {
  const date = new Date(value || Date.now());
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const display = (value, timezone) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Not recorded";
const requestId = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

function Field({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-[#58635d]">
      {label}
      {children}
    </label>
  );
}

function PersonForm({ initial, timezone, busy, save, close }) {
  const [values, setValues] = useState(initial);
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h3 className="font-bold">
        {values.id ? "Edit person" : "Add a person"}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          <input
            required
            maxLength={180}
            className={input}
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="Relationship">
          <input
            maxLength={120}
            className={input}
            value={values.relationship_label}
            onChange={(e) => set("relationship_label", e.target.value)}
          />
        </Field>
        <Field label="Email (optional)">
          <input
            type="email"
            className={input}
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label="Phone (optional)">
          <input
            type="tel"
            maxLength={80}
            className={input}
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
        <Field label="Check in every (days, optional)">
          <input
            type="number"
            min={1}
            max={365}
            className={input}
            value={values.check_in_days ?? ""}
            onChange={(e) =>
              set(
                "check_in_days",
                e.target.value ? Number(e.target.value) : null,
              )
            }
          />
        </Field>
        <Field label={`Check-in time (${timezone})`}>
          <input
            type="time"
            required
            className={input}
            value={values.check_in_time}
            onInput={(e) => set("check_in_time", e.target.value)}
            onChange={(e) => set("check_in_time", e.target.value)}
          />
        </Field>
      </div>
      <Field label="Interests and goals">
        <textarea
          maxLength={5000}
          className={input}
          rows={3}
          value={values.interests}
          onChange={(e) => set("interests", e.target.value)}
        />
      </Field>
      <Field label="Private notes">
        <textarea
          maxLength={10000}
          className={input}
          rows={3}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
      <p className="text-xs text-[#68746d]">
        Logging contact sets the next check-in from that date. Reminders appear
        in Attention; enable background monitoring for checks while you are
        away.
      </p>
      <label className="flex gap-2 text-xs">
        <input
          type="checkbox"
          checked={values.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
        />
        Reminders enabled for this person
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save person
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ContactForm({ person, busy, save, close }) {
  const [values, setValues] = useState(() => ({
    request_id: requestId(),
    occurred_at: localInput(),
    channel: "call",
    notes: "",
    follow_up_at: "",
    follow_up_note: "",
  }));
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="rounded-lg border border-[#dce3dd] bg-white p-4 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        save({
          ...values,
          occurred_at: new Date(values.occurred_at).toISOString(),
          follow_up_at: values.follow_up_at
            ? new Date(values.follow_up_at).toISOString()
            : null,
        });
      }}
    >
      <h3 className="font-bold">Log contact with {person.name}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Contact happened (device time)">
          <input
            required
            type="datetime-local"
            className={input}
            value={values.occurred_at}
            max={localInput()}
            onInput={(e) => set("occurred_at", e.target.value)}
            onChange={(e) => set("occurred_at", e.target.value)}
          />
        </Field>
        <Field label="How">
          <select
            className={input}
            value={values.channel}
            onChange={(e) => set("channel", e.target.value)}
          >
            {["call", "message", "visit", "email", "video", "other"].map(
              (value) => (
                <option key={value}>{value}</option>
              ),
            )}
          </select>
        </Field>
      </div>
      <Field label="Conversation notes">
        <textarea
          className={input}
          rows={3}
          maxLength={10000}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
      <Field label="Next follow-up (device time, optional)">
        <input
          type="datetime-local"
          className={input}
          value={values.follow_up_at}
          onInput={(e) => set("follow_up_at", e.target.value)}
          onChange={(e) => set("follow_up_at", e.target.value)}
        />
      </Field>
      <Field label="What to follow up on">
        <input
          className={input}
          maxLength={3000}
          value={values.follow_up_note}
          onChange={(e) => set("follow_up_note", e.target.value)}
        />
      </Field>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save contact
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ContactHistory({ person, timezone, run, busy }) {
  const history = useInfiniteQuery({
    queryKey: ["contacts", person.id],
    initialPageParam: null,
    queryFn: ({ pageParam }) => peopleService.contacts(person.id, pageParam),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  if (history.isLoading) return <p role="status">Loading contact history…</p>;
  if (history.error)
    return (
      <p role="alert">
        Could not load contact history.{" "}
        <button onClick={() => history.refetch()}>Try again</button>
      </p>
    );
  const items = history.data.pages.flatMap((page) => page.items);
  return (
    <section
      className="space-y-3"
      aria-label={`Contact history for ${person.name}`}
    >
      {!items.length && (
        <p className="text-sm text-[#68746d]">
          No contact recorded yet. Record your last conversation to start the
          cadence.
        </p>
      )}
      {items.map((contact) => (
        <article
          key={contact.id}
          className="rounded-lg border border-[#dce3dd] bg-white p-4"
        >
          <p className="text-sm font-bold">
            {contact.channel} · {display(contact.occurred_at, timezone)}
          </p>
          <p className="mt-2 whitespace-pre-wrap text-sm text-[#58635d]">
            {contact.notes}
          </p>
          {contact.follow_up_at && (
            <div className="mt-3 text-xs">
              <p>
                Follow-up: {display(contact.follow_up_at, timezone)} ·{" "}
                {contact.follow_up_done ? "Done" : "Pending"}
              </p>
              <p className="mt-1">{contact.follow_up_note}</p>
              <button
                className="secondary-button mt-2 text-xs"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    peopleService.followUp(contact.id, !contact.follow_up_done),
                  )
                }
              >
                {contact.follow_up_done
                  ? "Reopen follow-up"
                  : "Complete follow-up"}
              </button>
            </div>
          )}
          <button
            className="quiet-button mt-3 text-xs"
            disabled={busy}
            onClick={() => run(() => peopleService.removeContact(contact.id))}
          >
            Remove mistaken entry
          </button>
        </article>
      ))}
      {history.hasNextPage && (
        <button
          className="secondary-button"
          disabled={history.isFetchingNextPage}
          onClick={() => history.fetchNextPage()}
        >
          Older contacts
        </button>
      )}
    </section>
  );
}

function OccasionForm({ initial, people, busy, save, close }) {
  const [values, setValues] = useState(initial);
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h3 className="font-bold">
        {values.id ? "Edit occasion" : "Add an occasion"}
      </h3>
      <Field label="Occasion title">
        <input
          required
          className={input}
          maxLength={180}
          value={values.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="For">
          <select
            className={input}
            value={values.person_id ?? ""}
            onChange={(e) =>
              set("person_id", e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">No linked person</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Occasion kind">
          <select
            className={input}
            value={values.kind}
            onChange={(e) => set("kind", e.target.value)}
          >
            {["birthday", "anniversary", "other"].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Month">
          <input
            required
            type="number"
            min={1}
            max={12}
            className={input}
            value={values.month}
            onChange={(e) => set("month", Number(e.target.value))}
          />
        </Field>
        <Field label="Day">
          <input
            required
            type="number"
            min={1}
            max={31}
            className={input}
            value={values.day}
            onChange={(e) => set("day", Number(e.target.value))}
          />
        </Field>
        <Field
          label={
            values.annual ? "Birth / original year (optional)" : "Event year"
          }
        >
          <input
            required={!values.annual}
            type="number"
            min={1900}
            max={2200}
            className={input}
            value={values.year ?? ""}
            onChange={(e) =>
              set("year", e.target.value ? Number(e.target.value) : null)
            }
          />
        </Field>
      </div>
      <label className="flex gap-2 text-xs">
        <input
          type="checkbox"
          checked={values.annual}
          onChange={(e) => set("annual", e.target.checked)}
        />
        Repeat yearly
      </label>
      <Field label="Start preparation this many days before">
        <input
          required
          type="number"
          min={0}
          max={90}
          className={input}
          value={values.lead_days}
          onChange={(e) => set("lead_days", Number(e.target.value))}
        />
      </Field>
      <Field label="Occasion notes">
        <textarea
          className={input}
          rows={2}
          maxLength={10000}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
      <p className="text-xs text-[#68746d]">
        Age is shown only when a birth year is recorded. February 29 is observed
        on February 28 in other years. Preparation and the day itself have
        separate reminders.
      </p>
      <label className="flex gap-2 text-xs">
        <input
          type="checkbox"
          checked={values.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
        />
        Occasion reminders enabled
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save occasion
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function OccasionPlan({ occasion, run, busy, close, openAgent }) {
  const [values, setValues] = useState(occasion.preparation);
  const [query, setQuery] = useState(occasion.gift_research_query);
  const [location, setLocation] = useState("");
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <section
      className="surface-panel space-y-4 p-5"
      aria-label="Occasion preparation"
    >
      <h3 className="font-bold">
        Prepare for {occasion.title} · {occasion.on_date}
      </h3>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          run(async () => {
            await peopleService.prepare(occasion.id, values);
            close();
          });
        }}
      >
        <Field label="Gift or preparation plan">
          <textarea
            className={input}
            rows={2}
            maxLength={3000}
            value={values.gift_idea}
            onChange={(e) => set("gift_idea", e.target.value)}
          />
        </Field>
        <Field label="Gift budget (include currency)">
          <input
            className={input}
            maxLength={120}
            value={values.budget}
            onChange={(e) => set("budget", e.target.value)}
          />
        </Field>
        <Field label="Preparation notes">
          <textarea
            className={input}
            rows={2}
            maxLength={5000}
            value={values.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
        <Field label="Preparation status">
          <select
            className={input}
            value={values.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option value="pending">Still preparing</option>
            <option value="ready">Gift / preparation ready</option>
            <option value="completed">Occasion completed</option>
          </select>
        </Field>
        <button className="primary-button" disabled={busy}>
          Save preparation
        </button>
      </form>
      <form
        className="space-y-3 border-t border-[#dce3dd] pt-4"
        onSubmit={(event) => {
          event.preventDefault();
          run(async () => {
            await peopleService.giftResearch(occasion.id, {
              on_date: occasion.on_date,
              query,
              location,
            });
            close();
            openAgent();
          });
        }}
      >
        <h4 className="text-sm font-bold">Find useful gift options</h4>
        <Field label="Gift research request">
          <textarea
            required
            minLength={5}
            maxLength={2000}
            className={input}
            rows={4}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <Field label="Shopping location (optional)">
          <input
            className={input}
            maxLength={240}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </Field>
        <p className="text-xs leading-5 text-[#68746d]">
          Review the request above before sending it for web research. Save your
          budget first to include it in a new request. Research runs now, then
          daily at 09:00 in your account timezone when monitoring is on. It
          pauses when preparation is ready or the occasion has passed.
        </p>
        <button
          className="secondary-button"
          disabled={busy || occasion.preparation.status !== "pending"}
        >
          Research gifts
        </button>
      </form>
      <button className="quiet-button" onClick={close}>
        Close preparation
      </button>
    </section>
  );
}

export default function PeopleDesk({ run, busy, openAgent }) {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["people"],
    queryFn: peopleService.overview,
    refetchInterval: 60000,
  });
  const [editingPerson, setEditingPerson] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [logging, setLogging] = useState(false);
  const [editingOccasion, setEditingOccasion] = useState(null);
  const [plan, setPlan] = useState(null);
  const [removing, setRemoving] = useState(null);
  if (isLoading) return <p role="status">Loading people and occasions…</p>;
  if (error)
    return (
      <section className="surface-panel p-5">
        <p role="alert">Could not load people.</p>
        <button className="secondary-button mt-3" onClick={() => refetch()}>
          Try again
        </button>
      </section>
    );
  const selected = data.people.find((person) => person.id === selectedId);
  return (
    <div className="space-y-5">
      {removing && (
        <section
          className="surface-panel space-y-3 p-5"
          role="alertdialog"
          aria-label="Confirm removal"
        >
          <h3 className="font-bold">Remove {removing.name}?</h3>
          <p className="text-sm text-[#68746d]">
            {removing.kind === "person"
              ? "This deletes their contact history, linked occasions, preparation records and reminders."
              : "This deletes the occasion, preparation history, linked gift research and reminders."}
          </p>
          <div className="flex gap-2">
            <button
              disabled={busy}
              className="primary-button"
              onClick={() =>
                run(async () => {
                  if (removing.kind === "person") {
                    await peopleService.remove(removing.id);
                    if (selectedId === removing.id) setSelectedId(null);
                  } else {
                    await peopleService.removeOccasion(removing.id);
                  }
                  setPlan(null);
                  setRemoving(null);
                })
              }
            >
              Remove permanently
            </button>
            <button
              className="secondary-button"
              onClick={() => setRemoving(null)}
            >
              Keep it
            </button>
          </div>
        </section>
      )}
      <section className="surface-panel p-5">
        <p className="eyebrow">Keep in touch, prepare ahead</p>
        <h2 className="mt-2 text-xl font-bold">People and occasions</h2>
        <p className="mt-2 text-sm leading-6 text-[#68746d]">
          Remember your last conversation, follow up on what matters, and
          prepare for important dates. Contact history stays separate from
          reminders.
        </p>
        <button
          className="primary-button mt-4"
          onClick={() => setEditingPerson({ ...blankPerson })}
        >
          Add person
        </button>
      </section>
      {editingPerson && (
        <PersonForm
          key={editingPerson.id || "new"}
          initial={editingPerson}
          timezone={data.timezone}
          busy={busy}
          close={() => setEditingPerson(null)}
          save={(values) =>
            run(async () => {
              if (values.id) await peopleService.edit(values.id, values);
              else await peopleService.create(values);
              setEditingPerson(null);
            })
          }
        />
      )}
      {!data.people.length && (
        <p className="surface-panel p-5 text-sm text-[#68746d]">
          Add the people you want to keep in touch with. No contacts are
          inferred or prefilled.
        </p>
      )}
      <div className="grid gap-3 xl:grid-cols-2">
        {data.people.map((person) => (
          <article key={person.id} className="surface-panel p-5">
            <h3 className="font-bold">
              {person.name}{" "}
              {person.relationship_label && (
                <span className="text-xs font-normal text-[#68746d]">
                  · {person.relationship_label}
                </span>
              )}
            </h3>
            {!person.enabled && (
              <p className="mt-2 text-xs">Reminders paused</p>
            )}
            <p className="mt-3 text-xs text-[#68746d]">
              Last contact: {display(person.last_contact_at, data.timezone)}
            </p>
            {person.check_in_days && (
              <p className="mt-2 text-xs">
                Every {person.check_in_days} days ·{" "}
                {person.next_check_in_at
                  ? `Next: ${display(person.next_check_in_at, data.timezone)}`
                  : "Log previous contact to start"}
              </p>
            )}
            {person.interests && (
              <p className="mt-3 text-sm text-[#58635d]">{person.interests}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="secondary-button text-xs"
                onClick={() => {
                  setSelectedId(person.id);
                  setLogging(true);
                }}
              >
                Log contact
              </button>
              <button
                className="quiet-button text-xs"
                onClick={() => {
                  setSelectedId(person.id);
                  setLogging(false);
                }}
              >
                History
              </button>
              <button
                className="quiet-button text-xs"
                onClick={() => setEditingPerson(person)}
              >
                Edit person
              </button>
              <button
                className="quiet-button text-xs"
                disabled={busy}
                onClick={() =>
                  setRemoving({
                    kind: "person",
                    id: person.id,
                    name: person.name,
                  })
                }
              >
                Remove person
              </button>
            </div>
          </article>
        ))}
      </div>
      {selected && (
        <section className="surface-panel space-y-4 p-5">
          <div className="flex justify-between gap-3">
            <h2 className="font-bold">Contact history · {selected.name}</h2>
            <button
              className="quiet-button text-xs"
              onClick={() => setSelectedId(null)}
            >
              Close history
            </button>
          </div>
          {logging && (
            <ContactForm
              key={selected.id}
              person={selected}
              busy={busy}
              close={() => setLogging(false)}
              save={(values) =>
                run(async () => {
                  await peopleService.logContact(selected.id, values);
                  setLogging(false);
                })
              }
            />
          )}
          <ContactHistory
            person={selected}
            timezone={data.timezone}
            run={run}
            busy={busy}
          />
        </section>
      )}
      <section className="space-y-3">
        <div className="flex justify-between gap-3">
          <h2 className="font-bold">Birthdays and important dates</h2>
          <button
            className="secondary-button"
            onClick={() => setEditingOccasion({ ...blankOccasion })}
          >
            Add occasion
          </button>
        </div>
        {editingOccasion && (
          <OccasionForm
            key={editingOccasion.id || "new"}
            initial={editingOccasion}
            people={data.people}
            busy={busy}
            close={() => setEditingOccasion(null)}
            save={(values) =>
              run(async () => {
                if (values.id)
                  await peopleService.editOccasion(values.id, values);
                else await peopleService.createOccasion(values);
                setEditingOccasion(null);
              })
            }
          />
        )}
        {plan && (
          <OccasionPlan
            key={`${plan.id}:${plan.on_date}`}
            occasion={plan}
            run={run}
            busy={busy}
            close={() => setPlan(null)}
            openAgent={openAgent}
          />
        )}
        {!data.occasions.length && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            Record a date and preparation window to get advance reminders.
          </p>
        )}
        {data.occasions.map((occasion) => (
          <article key={occasion.id} className="surface-panel p-5">
            <div className="flex justify-between gap-3">
              <h3 className="font-bold">{occasion.title}</h3>
              <span className="status-pill">
                {occasion.enabled ? occasion.preparation.status : "Paused"}
              </span>
            </div>
            <p className="mt-2 text-sm">
              {occasion.on_date}
              {occasion.turning_age != null
                ? ` · Age ${occasion.turning_age}`
                : ""}
              {occasion.person_name ? ` · ${occasion.person_name}` : ""}
            </p>
            <p className="mt-2 text-xs text-[#68746d]">
              Prepare {occasion.lead_days} days ahead ·{" "}
              {occasion.annual ? "Yearly" : "One time"}
            </p>
            {occasion.preparation.gift_idea && (
              <p className="mt-3 text-sm">{occasion.preparation.gift_idea}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="secondary-button text-xs"
                onClick={() => setPlan(occasion)}
              >
                Plan / gift research
              </button>
              <button
                className="quiet-button text-xs"
                onClick={() => setEditingOccasion(occasion)}
              >
                Edit occasion
              </button>
              <button
                className="quiet-button text-xs"
                disabled={busy}
                onClick={() =>
                  setRemoving({
                    kind: "occasion",
                    id: occasion.id,
                    name: occasion.title,
                  })
                }
              >
                Remove occasion
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
